import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { SearchResult, CropBox } from '../api'

export type SearchMode = 'text' | 'image' | 'region' | 'face' | 'person' | null

interface SearchState {
  // Search mode and active state
  mode: SearchMode
  isSearching: boolean
  isSearchActive: boolean

  // Text search
  textQuery: string

  // Image-to-image search
  sourceImageId: string | null

  // Region search
  regionSourceImageId: string | null
  cropBox: CropBox | null
  isSelectingRegion: boolean

  // Face search
  sourceFaceId: string | null
  sourceFaceCropUrl: string | null

  // Person search
  sourcePersonId: string | null
  sourcePersonName: string | null

  // Results
  results: SearchResult[]
  error: string | null

  // UI state
  showResultsPanel: boolean
}

const initialState: SearchState = {
  mode: null,
  isSearching: false,
  isSearchActive: false,

  textQuery: '',

  sourceImageId: null,

  regionSourceImageId: null,
  cropBox: null,
  isSelectingRegion: false,

  sourceFaceId: null,
  sourceFaceCropUrl: null,

  sourcePersonId: null,
  sourcePersonName: null,

  results: [],
  error: null,

  showResultsPanel: false,
}

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    // Text search actions
    setTextQuery: (state, action: PayloadAction<string>) => {
      state.textQuery = action.payload
    },

    startTextSearch: (state, action: PayloadAction<string>) => {
      state.mode = 'text'
      state.textQuery = action.payload
      state.isSearching = true
      state.isSearchActive = true
      state.showResultsPanel = true
      state.error = null
    },

    // Face search actions
    startFaceSearch: (state, action: PayloadAction<{ faceId: string; cropUrl: string }>) => {
      state.mode = 'face'
      state.sourceFaceId = action.payload.faceId
      state.sourceFaceCropUrl = action.payload.cropUrl
      state.isSearching = true
      state.isSearchActive = true
      state.showResultsPanel = true
      state.error = null
    },

    // Person search actions
    startPersonSearch: (state, action: PayloadAction<{ personId: string; personName: string | null }>) => {
      state.mode = 'person'
      state.sourcePersonId = action.payload.personId
      state.sourcePersonName = action.payload.personName
      state.isSearching = true
      state.isSearchActive = true
      state.showResultsPanel = true
      state.error = null
    },

    // Image-to-image search actions
    startImageSearch: (state, action: PayloadAction<string>) => {
      state.mode = 'image'
      state.sourceImageId = action.payload
      state.isSearching = true
      state.isSearchActive = true
      state.showResultsPanel = true
      state.error = null
    },

    // Region search actions
    startRegionSelection: (state, action: PayloadAction<string>) => {
      state.mode = 'region'
      state.regionSourceImageId = action.payload
      state.isSelectingRegion = true
      state.cropBox = null
    },

    setCropBox: (state, action: PayloadAction<CropBox | null>) => {
      state.cropBox = action.payload
    },

    confirmRegionSearch: (state) => {
      if (state.cropBox && state.regionSourceImageId) {
        state.isSelectingRegion = false
        state.isSearching = true
        state.isSearchActive = true
        state.showResultsPanel = true
        state.error = null
      }
    },

    cancelRegionSelection: (state) => {
      state.isSelectingRegion = false
      state.cropBox = null
      if (!state.isSearchActive) {
        state.mode = null
        state.regionSourceImageId = null
      }
    },

    // Results actions
    setSearchResults: (state, action: PayloadAction<SearchResult[]>) => {
      state.results = action.payload
      state.isSearching = false
    },

    setSearchError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isSearching = false
    },

    // UI actions
    showResults: (state) => {
      state.showResultsPanel = true
    },

    hideResults: (state) => {
      state.showResultsPanel = false
    },

    // Clear search
    clearSearch: (state) => {
      state.mode = null
      state.isSearching = false
      state.isSearchActive = false
      state.textQuery = ''
      state.sourceImageId = null
      state.regionSourceImageId = null
      state.cropBox = null
      state.isSelectingRegion = false
      state.sourceFaceId = null
      state.sourceFaceCropUrl = null
      state.sourcePersonId = null
      state.sourcePersonName = null
      state.results = []
      state.error = null
      state.showResultsPanel = false
    },
  },
})

export const {
  setTextQuery,
  startTextSearch,
  startFaceSearch,
  startPersonSearch,
  startImageSearch,
  startRegionSelection,
  setCropBox,
  confirmRegionSearch,
  cancelRegionSelection,
  setSearchResults,
  setSearchError,
  showResults,
  hideResults,
  clearSearch,
} = searchSlice.actions

export default searchSlice.reducer
